import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiFetch } from '@/utils/api';
import { ArrowLeft, Save } from 'lucide-react';
import { CurrentUserProvider } from '@/hooks/useCurrentUser';
import { Skeleton } from '@/components/ui/skeleton';

interface Vendor {
    id: string;
    name: string;
}

interface CostCenter {
    id: string;
    name: string;
    is_active: boolean;
}

interface PaymentInstrument {
    id: string;
    name: string;
    instrument_type: string;
}

interface PaymentFormData {
    cost_center_id: string;
    vendor_id: string;
    direction: string;
    amount: string;
    currency: string;
    payment_instrument_id: string;
    value_date: string;
    received_at: string;
    status: string;
    invoice_reference: string;
    external_id: string;
}

const directionOptions = [
    { value: 'AR', label: 'Accounts Receivable (Money In)' },
    { value: 'AP', label: 'Accounts Payable (Money Out)' },
];

const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'posted', label: 'Posted' },
    { value: 'voided', label: 'Voided' },
    { value: 'reversed', label: 'Reversed' },
];

const currencyOptions = [
    { value: 'HUF', label: 'HUF (Hungarian Forint)' },
    { value: 'EUR', label: 'EUR (Euro)' },
    { value: 'USD', label: 'USD (US Dollar)' },
];

function isoDateOnly(iso: string | undefined): string {
    if (!iso) return '';
    return iso.slice(0, 10);
}

export default function EditPaymentPage() {
    const navigate = useNavigate();
    const params = useParams({ strict: false });
    const id = params.id as string;

    const [formData, setFormData] = useState<PaymentFormData | null>(null);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [paymentInstruments, setPaymentInstruments] = useState<PaymentInstrument[]>([]);
    const [pageLoading, setPageLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!id) return;

        const load = async () => {
            setPageLoading(true);
            setLoadError(null);
            try {
                const [paymentRes, vendorsResponse, costCentersResponse] = await Promise.all([
                    apiFetch(`/api/payments/${id}`),
                    apiFetch('/api/vendors/'),
                    apiFetch('/api/cost-centers/'),
                ]);

                if (!paymentRes.ok) {
                    setLoadError('Payment not found');
                    setPageLoading(false);
                    return;
                }

                const payment = await paymentRes.json();

                if (vendorsResponse.ok) {
                    const vendorsData = await vendorsResponse.json();
                    setVendors(vendorsData.vendors || []);
                }
                if (costCentersResponse.ok) {
                    const costCentersData = await costCentersResponse.json();
                    setCostCenters(costCentersData.cost_centers || []);
                }

                try {
                    const instrumentsResponse = await apiFetch('/api/payment-instruments/');
                    if (instrumentsResponse.ok) {
                        const instrumentsData = await instrumentsResponse.json();
                        setPaymentInstruments(instrumentsData.payment_instruments || []);
                    }
                } catch {
                    setPaymentInstruments([]);
                }

                setFormData({
                    cost_center_id: payment.cost_center?.id ?? '',
                    vendor_id: payment.vendor?.id ?? '',
                    direction: payment.direction || 'AP',
                    amount: String(payment.amount ?? ''),
                    currency: payment.currency || 'HUF',
                    payment_instrument_id: payment.payment_instrument?.id ?? '',
                    value_date: isoDateOnly(payment.value_date) || new Date().toISOString().split('T')[0],
                    received_at: isoDateOnly(payment.received_at),
                    status: payment.status || 'posted',
                    invoice_reference: payment.invoice_reference ?? '',
                    external_id: payment.external_id ?? '',
                });
            } catch (e) {
                console.error(e);
                setLoadError('Failed to load payment');
            } finally {
                setPageLoading(false);
            }
        };

        void load();
    }, [id]);

    const handleInputChange = (field: keyof PaymentFormData, value: string) => {
        setFormData((prev) => (prev ? { ...prev, [field]: value } : prev));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = (data: PaymentFormData): boolean => {
        const newErrors: Record<string, string> = {};
        if (!data.cost_center_id) newErrors.cost_center_id = 'Cost center is required';
        if (!data.direction) newErrors.direction = 'Direction is required';
        if (!data.amount || parseFloat(data.amount) <= 0) newErrors.amount = 'Amount must be greater than 0';
        if (!data.currency) newErrors.currency = 'Currency is required';
        if (!data.value_date) newErrors.value_date = 'Value date is required';
        if (!data.status) newErrors.status = 'Status is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData) return;
        if (!validateForm(formData)) return;

        setSubmitLoading(true);
        try {
            const patchBody = {
                cost_center_id: formData.cost_center_id || null,
                vendor_id: formData.vendor_id || null,
                direction: formData.direction,
                amount: parseFloat(formData.amount),
                currency: formData.currency,
                payment_instrument_id: formData.payment_instrument_id || null,
                value_date: new Date(formData.value_date).toISOString(),
                received_at: formData.received_at
                    ? new Date(formData.received_at).toISOString()
                    : null,
                status: formData.status,
                invoice_reference: formData.invoice_reference || null,
                external_id: formData.external_id || null,
            };

            const response = await apiFetch(`/api/payments/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(patchBody),
            });

            if (response.ok) {
                navigate({ to: `/payments/${id}` });
            } else {
                let msg = 'Failed to update payment';
                try {
                    const err = await response.json();
                    if (typeof err.detail === 'string') msg = err.detail;
                } catch {
                    /* ignore */
                }
                alert(msg);
            }
        } catch (error) {
            console.error('Error updating payment:', error);
            alert('Failed to update payment');
        } finally {
            setSubmitLoading(false);
        }
    };

    if (pageLoading) {
        return (
            <CurrentUserProvider>
                <Layout>
                    <div className="space-y-6 p-6">
                        <Skeleton className="h-10 w-64" />
                        <Skeleton className="h-64 w-full" />
                    </div>
                </Layout>
            </CurrentUserProvider>
        );
    }

    if (loadError || !formData) {
        return (
            <CurrentUserProvider>
                <Layout>
                    <div className="space-y-6 p-6">
                        <p className="text-muted-foreground">{loadError || 'Payment not found'}</p>
                        <Button variant="outline" onClick={() => navigate({ to: '/payments' })}>
                            Back to payments
                        </Button>
                    </div>
                </Layout>
            </CurrentUserProvider>
        );
    }

    return (
        <CurrentUserProvider>
            <Layout>
                <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                        <Button variant="ghost" onClick={() => navigate({ to: `/payments/${id}` })}>
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <h1 className="text-3xl font-bold tracking-tight">Edit Payment</h1>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Payment Information</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="direction">Direction *</Label>
                                        <Select
                                            value={formData.direction}
                                            onValueChange={(value) => handleInputChange('direction', value)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select direction" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {directionOptions.map((option) => (
                                                    <SelectItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.direction && (
                                            <p className="text-sm text-red-500">{errors.direction}</p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="amount">Amount *</Label>
                                            <Input
                                                id="amount"
                                                type="number"
                                                step="0.01"
                                                value={formData.amount}
                                                onChange={(e) => handleInputChange('amount', e.target.value)}
                                            />
                                            {errors.amount && (
                                                <p className="text-sm text-red-500">{errors.amount}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="currency">Currency *</Label>
                                            <Select
                                                value={formData.currency}
                                                onValueChange={(value) => handleInputChange('currency', value)}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select currency" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {currencyOptions.map((option) => (
                                                        <SelectItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {errors.currency && (
                                                <p className="text-sm text-red-500">{errors.currency}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="status">Status *</Label>
                                        <Select
                                            value={formData.status}
                                            onValueChange={(value) => handleInputChange('status', value)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select status" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {statusOptions.map((option) => (
                                                    <SelectItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.status && (
                                            <p className="text-sm text-red-500">{errors.status}</p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="value_date">Value Date *</Label>
                                            <Input
                                                id="value_date"
                                                type="date"
                                                value={formData.value_date}
                                                onChange={(e) => handleInputChange('value_date', e.target.value)}
                                            />
                                            {errors.value_date && (
                                                <p className="text-sm text-red-500">{errors.value_date}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="received_at">Received At</Label>
                                            <Input
                                                id="received_at"
                                                type="date"
                                                value={formData.received_at}
                                                onChange={(e) => handleInputChange('received_at', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Related Information</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="cost_center_id">Cost Center *</Label>
                                        <Select
                                            value={formData.cost_center_id}
                                            onValueChange={(value) => handleInputChange('cost_center_id', value)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select cost center" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {costCenters
                                                    .filter((cc) => cc.is_active)
                                                    .map((costCenter) => (
                                                        <SelectItem key={costCenter.id} value={costCenter.id}>
                                                            {costCenter.name}
                                                        </SelectItem>
                                                    ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.cost_center_id && (
                                            <p className="text-sm text-red-500">{errors.cost_center_id}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="vendor_id">Vendor</Label>
                                        <Select
                                            value={formData.vendor_id}
                                            onValueChange={(value) => handleInputChange('vendor_id', value)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select vendor (optional)" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="">No vendor</SelectItem>
                                                {vendors.map((vendor) => (
                                                    <SelectItem key={vendor.id} value={vendor.id}>
                                                        {vendor.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {paymentInstruments.length > 0 && (
                                        <div className="space-y-2">
                                            <Label htmlFor="payment_instrument_id">Payment Method</Label>
                                            <Select
                                                value={formData.payment_instrument_id}
                                                onValueChange={(value) =>
                                                    handleInputChange('payment_instrument_id', value)
                                                }
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select payment method (optional)" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="">No payment method</SelectItem>
                                                    {paymentInstruments.map((instrument) => (
                                                        <SelectItem key={instrument.id} value={instrument.id}>
                                                            {instrument.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label htmlFor="invoice_reference">Invoice Reference</Label>
                                        <Input
                                            id="invoice_reference"
                                            value={formData.invoice_reference}
                                            onChange={(e) => handleInputChange('invoice_reference', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="external_id">External ID</Label>
                                        <Input
                                            id="external_id"
                                            value={formData.external_id}
                                            onChange={(e) => handleInputChange('external_id', e.target.value)}
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="flex justify-end space-x-4 mt-6">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate({ to: `/payments/${id}` })}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={submitLoading}>
                                {submitLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4 mr-2" />
                                        Save changes
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </Layout>
        </CurrentUserProvider>
    );
}

export const Route = createFileRoute('/payments/$id/edit')({
    component: EditPaymentPage,
})
